import { useEffect, useRef } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

const EMERALD = "#059669";
const COBALT = "#2563EB";

function pinHtml(type, selected) {
  const color = type === "jobad" ? COBALT : EMERALD;
  const inner =
    type === "jobad"
      ? `<rect x="11" y="13" width="14" height="10" rx="1.5" fill="${color}"/><rect x="14" y="10.5" width="8" height="3" rx="1" fill="${color}"/><rect x="15.5" y="16.5" width="5" height="2" rx="0.5" fill="#fff"/>`
      : `<path d="M18 11l1.6 3.4L23 16l-3.4 1.6L18 21l-1.6-3.4L13 16l3.4-1.6z" fill="${color}"/>`;
  const cls = `jk-pin${selected ? " jk-pin--selected" : ""}`;
  return `<span class="${cls}"><svg width="36" height="46" viewBox="0 0 36 46" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 0C8.6 0 1 7.6 1 17c0 11.9 17 29 17 29s17-17.1 17-29C35 7.6 27.4 0 18 0z" fill="${color}"/>
    <circle cx="18" cy="16.5" r="11.5" fill="#ffffff"/>
    ${inner}
  </svg></span>`;
}

function makeIcon(type, selected) {
  return L.divIcon({
    html: pinHtml(type, selected),
    className: "jk-pin-wrap",
    iconSize: [36, 46],
    iconAnchor: [18, 46],
    popupAnchor: [0, -44],
  });
}

export default function MapPanel({ items, center, cities, selectedId, onSelect }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const markerIndex = useRef({});

  // init map once
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: center || [62.0, 15.0],
      zoom: 12,
      zoomControl: true,
      scrollWheelZoom: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    // Ensure tiles reflow whenever the container gets/changes its real size
    // (fixes blank tiles after the auto-zoom animation or view toggles).
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        if (mapRef.current) {
          try { mapRef.current.invalidateSize(); } catch { /* map not ready */ }
        }
      });
      ro.observe(containerRef.current);
    }
    const t = setTimeout(() => {
      if (mapRef.current) {
        try { mapRef.current.invalidateSize(); } catch { /* map not ready */ }
      }
    }, 300);
    return () => {
      clearTimeout(t);
      if (ro) ro.disconnect();
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // render markers on items change
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    markerIndex.current = {};
    items.forEach((it) => {
      if (typeof it.lat !== "number" || typeof it.lon !== "number") return;
      const marker = L.marker([it.lat, it.lon], {
        icon: makeIcon(it.type, it.id === selectedId),
        bubblingMouseEvents: false,
        riseOnHover: true,
      });
      marker.on("click", (e) => {
        if (e && e.originalEvent) {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);
        }
        // Defer to next tick so the originating pointer event finishes before the
        // Radix dialog mounts its "pointer down outside" listener (prevents instant close).
        setTimeout(() => onSelect(it), 0);
      });
      marker.addTo(layer);
      markerIndex.current[it.id] = marker;
    });
    const t = setTimeout(() => {
      if (mapRef.current) {
        try { mapRef.current.invalidateSize(); } catch { /* map not ready */ }
      }
    }, 120);
    return () => clearTimeout(t);
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  // auto-zoom to the selected city/cities whenever the city selection changes
  const citiesKey = (cities || []).map((c) => c.id).join(",");
  useEffect(() => {
    if (!cities || cities.length === 0) return;
    const pts = cities.map((c) => [c.lat, c.lon]);
    // Re-read the live map inside the callback and verify its panes are laid out
    // (el._leaflet_pos exists) before changing the view – guards against the
    // Leaflet crash when a deferred call fires on an unmounted/not-ready map.
    const applyView = () => {
      const map = mapRef.current;
      if (!map || !map._loaded || !map._mapPane) return;
      try {
        map.invalidateSize();
        if (pts.length === 1) {
          map.setView(pts[0], 12, { animate: false });
        } else {
          map.fitBounds(L.latLngBounds(pts), { padding: [80, 80], maxZoom: 11, animate: false });
        }
      } catch { /* map not ready yet – a later tick will apply the view */ }
    };
    applyView();
    const t = setTimeout(applyView, 400);
    return () => clearTimeout(t);
  }, [citiesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // update selected marker styles & focus
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    items.forEach((it) => {
      const m = markerIndex.current[it.id];
      if (m) m.setIcon(makeIcon(it.type, it.id === selectedId));
    });
    if (selectedId && markerIndex.current[selectedId] && map._mapPane) {
      const m = markerIndex.current[selectedId];
      try { map.panTo(m.getLatLng(), { animate: false }); } catch { /* map not ready */ }
    }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={containerRef}
      data-testid="leaflet-map-container"
      className="h-full w-full"
      style={{ minHeight: "100%" }}
    />
  );
}
